import { useState } from "react";
import { Loader2, X, Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import * as XLSX from "xlsx";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_BADGE = {
  ready: { label: "Ready to Import", cls: "bg-emerald-100 text-emerald-700" },
  exists: { label: "Already Exists", cls: "bg-amber-100 text-amber-700" },
  invalid: { label: "Invalid Mobile Number", cls: "bg-red-100 text-red-700" },
};

const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[.\s_]/g, "");
const SHOP_KEYS = ["particulars", "shopname", "name"];
const MOBILE_KEYS = ["mobilenos", "mobileno", "mobilenumber", "mobile", "phone", "phoneno", "contactno", "contact"];

function findCol(headerRow, keys) {
  for (let i = 0; i < headerRow.length; i++) {
    if (keys.includes(norm(headerRow[i]))) return i;
  }
  return -1;
}

export default function ImportCustomersModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);      // parsed [{shop_name, mobile, status}]
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null); // {done, total}

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setSummary(null); setRows(null); setParsing(true); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      // Read as rows-of-arrays so we can skip Tally title rows and find the real header row.
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });

      let headerIdx = -1, shopCol = -1, mobileCol = -1;
      for (let i = 0; i < aoa.length; i++) {
        const sc = findCol(aoa[i], SHOP_KEYS);
        const mc = findCol(aoa[i], MOBILE_KEYS);
        if (sc !== -1 && mc !== -1) { headerIdx = i; shopCol = sc; mobileCol = mc; break; }
      }
      if (headerIdx === -1) {
        setError("Could not find the 'Particulars' and 'Mobile Nos.' columns anywhere in the sheet. Please check the file.");
        setParsing(false);
        return;
      }

      const raw = [];
      for (let i = headerIdx + 1; i < aoa.length; i++) {
        const row = aoa[i];
        const shop = String(row[shopCol] ?? "").trim();
        const mobile = String(row[mobileCol] ?? "").trim();
        if (!shop && !mobile) continue;            // ignore blank rows
        raw.push({ shop_name: shop, mobile });
      }
      if (raw.length === 0) {
        setError("Found the header row but there are no data rows below it.");
        setParsing(false);
        return;
      }
      const { data } = await api.post("/customers/import/preview", { rows: raw });
      setRows(data.rows);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the Excel file. Please upload a valid .xlsx file.");
    } finally { setParsing(false); }
  };

  const confirmImport = async () => {
    if (!rows) return;
    setImporting(true); setError("");
    const ready = rows.filter((r) => r.status === "ready").map((r) => ({ shop_name: r.shop_name, mobile: r.mobile }));
    const previewExisting = rows.filter((r) => r.status === "exists").length;
    const invalidCount = rows.filter((r) => r.status === "invalid").length;
    const acc = { total: rows.length, imported: 0, existing: previewExisting, invalid: invalidCount };
    const CHUNK = 50;
    setProgress({ done: 0, total: ready.length });
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const batch = ready.slice(i, i + CHUNK);
        // Retry-safe: backend skips mobiles that already exist, so re-sending never duplicates.
        const { data } = await api.post("/customers/import", { rows: batch });
        acc.imported += data.imported;
        acc.existing += data.existing;
        setProgress({ done: Math.min(i + CHUNK, ready.length), total: ready.length });
      }
      setSummary(acc);
      onImported && onImported();
    } catch (err) {
      setError((formatApiErrorDetail(err.response?.data?.detail) || "Import interrupted") + `. Imported ${acc.imported} so far — re-upload the same file to finish the rest (already-imported customers are skipped).`);
    } finally { setImporting(false); }
  };

  const counts = rows ? rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}) : {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div data-testid="import-customers-modal" className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-5">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Import Customers from Excel</h3>
            <p className="text-xs text-slate-500">Upload a Tally .xlsx export — uses <b>Particulars</b> → Shop Name and <b>Mobile Nos.</b> → Mobile Number.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto p-5">
          {!summary && (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 text-sm font-semibold text-blue-700 hover:bg-blue-50">
              {parsing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              {parsing ? "Reading file..." : fileName ? `Change file (${fileName})` : "Choose .xlsx file"}
              <input data-testid="import-file-input" type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }} />
            </label>
          )}

          {error && <p data-testid="import-error" className="mt-3 text-sm font-medium text-red-600">{error}</p>}

          {/* Summary after import */}
          {summary && (
            <div data-testid="import-summary" className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <h4 className="text-lg font-bold text-slate-800">Import complete</h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-slate-100 px-4 py-2"><p className="text-xl font-extrabold text-slate-800" data-testid="summary-total">{summary.total}</p><p className="text-[11px] text-slate-500">Total Rows</p></div>
                <div className="rounded-lg bg-emerald-50 px-4 py-2"><p className="text-xl font-extrabold text-emerald-700" data-testid="summary-imported">{summary.imported}</p><p className="text-[11px] text-slate-500">Imported</p></div>
                <div className="rounded-lg bg-amber-50 px-4 py-2"><p className="text-xl font-extrabold text-amber-700" data-testid="summary-existing">{summary.existing}</p><p className="text-[11px] text-slate-500">Already Existing</p></div>
                <div className="rounded-lg bg-red-50 px-4 py-2"><p className="text-xl font-extrabold text-red-700" data-testid="summary-invalid">{summary.invalid}</p><p className="text-[11px] text-slate-500">Invalid/Skipped</p></div>
              </div>
              <button data-testid="import-done-button" onClick={onClose} className="mt-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Done</button>
            </div>
          )}

          {/* Preview table */}
          {rows && !summary && (
            <>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">Ready: {counts.ready || 0}</span>
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700">Already Exists: {counts.exists || 0}</span>
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700">Invalid: {counts.invalid || 0}</span>
              </div>
              <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-slate-100">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="px-3 py-2">Shop Name</th><th className="px-3 py-2">Mobile Number</th><th className="px-3 py-2">Import Status</th></tr>
                  </thead>
                  <tbody data-testid="import-preview-table">
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-50">
                        <td className="px-3 py-2 text-slate-700">{r.shop_name || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2 text-slate-600">{r.mobile || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_BADGE[r.status].cls}`}>{STATUS_BADGE[r.status].label}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {rows && !summary && (
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 p-5">
            {importing && progress ? (
              <p data-testid="import-progress" className="text-sm font-semibold text-blue-700">
                Importing… {progress.done}/{progress.total} imported
              </p>
            ) : (
              <p className="text-xs text-slate-500"><b>{counts.ready || 0}</b> customer(s) will be imported as Approved.</p>
            )}
            <div className="flex gap-2">
              <button onClick={onClose} disabled={importing} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button data-testid="confirm-import-button" onClick={confirmImport} disabled={importing || !(counts.ready > 0)}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                {importing ? `Importing ${progress ? `${progress.done}/${progress.total}` : ""}` : `Confirm Import (${counts.ready || 0})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
